import {ChangeDetectionStrategy, Component, OnInit} from '@angular/core';
import {Store} from '@ngrx/store';
import {AppState} from '@store/app.states';
import {AddUserRequest, CloseCreateUserDialog} from '@app/4dm1n/users-list/store/users-list.actions';
import {Observable} from 'rxjs';
import {createUserDialogOpenedStatus} from '@app/4dm1n/users-list/store/users-list.selector';
import {FormControl, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {map} from 'rxjs/operators';
import {OnDestroyMixin, untilComponentDestroyed} from '@w11k/ngx-componentdestroyed';
import {UsersListService} from '@app/4dm1n/users-list/users-list.service';
import {ToastrService} from 'ngx-toastr';
import {userRolesGetAsKeyValue} from '../../../../@shared/_referencesLists/userRoles/userRoles';
import {
  IOrganization,
  OrganizationsService
} from '../../../../@shared/_referencesLists/reference-lists/organizations.service';

@Component({
    selector: 'app-user-create',
    templateUrl: './user-create.component.html',
    styleUrls: ['./user-create.component.scss'],
    standalone: false
})
export class UserCreateComponent extends OnDestroyMixin implements OnInit {
  constructor(
    private store: Store<AppState>,
    private usersListService: UsersListService,
    private toastr: ToastrService,
    private organizationsService: OrganizationsService
  ) {
    super();

    this.userRolesKeyValue = Object.keys(this.usersRoles).map((key) => {
      return {
        role: key,
        description: this.usersRoles[key]
      };
    });

  }

  // Roles utilisateurs
  private usersRoles = userRolesGetAsKeyValue;
  userRolesKeyValue = [];
  userRolesSelected =
    [
      'SURVEY_MULTI',
      'SURVEY_IDEA', 'SURVEY_COMBAT', 'SURVEY_BENCHMARK', 'SURVEY_KANO', // <----- Pas sûr que ça serve encore ?
      'SURVEY_EXCEL_EXPORT',
      'BASIC_ACCOUNT',
      'SAAS_PUBLICATION',
      'SHORTCUT_TO_V2',
      'V2_DETAILED_RESPONSES',
      'V2_RESULT_DASHBOARD'];

  private validatorRules = {
    email: {maxLength: 100},
    firstName: {maxLength: 100, minLength: 2},
    lastName: {maxLength: 100, minLength: 2},
    password: {minLength: 4, maxLength: 20},
    organization: {minLength: 1, maxLength: 100}
  };

  private errorMessages = {
    email: {
      format: `Le format du courriel n'est pas valide`,
      length: `Le courriel ne doit contenir plus de ${this.validatorRules.email.maxLength}`,
      required: `Le courriel est requis`,
      unique: `Ce courriel existe déjà`
    },
    firstName: {
      length: `Le prénom doit contenir de ${this.validatorRules.firstName.minLength} à ${this.validatorRules.firstName.maxLength} caractères`,
      required: `Le prénom est requis`
    },
    lastName: {
      length: `Le nom doit contenir de ${this.validatorRules.lastName.minLength} à ${this.validatorRules.lastName.maxLength} caractères`,
      required: `Le nom est requis`
    },
    organization: {
      required: `L'organisation est requise`,
      length: `Le nom de l'organisation doit contenir de ${this.validatorRules.organization.minLength} à ${this.validatorRules.organization.maxLength} caractères`
    },
    password: {
      required: `Le mot de passe est requis`,
      length: `Le mot de passe doit faire de ${this.validatorRules.password.minLength} à ${this.validatorRules.password.maxLength}`
    },
    passwordConfirmation: {
      required: `La confirmation du mot de passe est requise`,
      same: `Le mot de passe de confirmation ne correspond pas`
    },
    accountType: {
      required: `Le type de compte est requis`
    }
  };

  createUserDialogOpened$: Observable<boolean> = this.store.select(createUserDialogOpenedStatus).pipe(untilComponentDestroyed(this));
  filteredOrganizations$: Observable<IOrganization[]>;

  organizations: IOrganization[] = [];

  availableLang = [{value: 'fr_CA', name: 'FR'}, {value: 'en_EN', name: 'EN'}];

  userCreateForm = new UntypedFormGroup({
    firstName: new UntypedFormControl(null, [
      Validators.required,
      Validators.minLength(this.validatorRules.firstName.minLength),
      Validators.maxLength(this.validatorRules.firstName.maxLength)
    ]),
    lastName: new UntypedFormControl(null, [
      Validators.required,
      Validators.minLength(this.validatorRules.lastName.minLength),
      Validators.maxLength(this.validatorRules.lastName.maxLength)
    ]),
    email: new UntypedFormControl(null, [
      Validators.required,
      Validators.email,
      Validators.maxLength(this.validatorRules.email.maxLength)
    ], [
      this.uniqueEmailValidator.bind(this)
    ]),
    newOrganization: new FormControl(false),
    organization: new UntypedFormControl(null, [
      Validators.required,
      Validators.minLength(this.validatorRules.organization.minLength),
      Validators.maxLength(this.validatorRules.organization.maxLength)
    ]),
    saasStatus: new UntypedFormControl(0, [
      Validators.required
    ]),
    password: new UntypedFormControl(null, [
      Validators.required,
      Validators.minLength(this.validatorRules.password.minLength),
      Validators.maxLength(this.validatorRules.password.maxLength)
    ]),

    lang: new UntypedFormControl('fr_CA'),
    userRoles: new UntypedFormControl(this.userRolesSelected)

  });

  ngOnInit(): void {
    this.organizationsService.organizationsList$
      .pipe(untilComponentDestroyed(this)).subscribe(organizations => {
      this.organizations = Object.values(organizations);
    });

    this.filteredOrganizations$ = this.userCreateForm.get('organization').valueChanges
      .pipe(map(textInput => this.organizationAutoCompleteFilter(textInput)));
  }

  private organizationAutoCompleteFilter(textInput: string): IOrganization[] {
    const filterValue = textInput ? textInput.toString().toLowerCase() : '';
    return this.organizations.filter(org => org.name.toLowerCase().includes(filterValue));
  }

  closeCreateUserDialog(): void {
    this.store.dispatch(CloseCreateUserDialog());
    this.userCreateForm.reset();
    this.userCreateForm.get('lang').setValue('fr_CA');
    this.userCreateForm.get('userRoles').setValue(this.userRolesSelected);
    this.userCreateForm.get('saasStatus').setValue(0);
  }

  createUser() {

    if (this.userCreateForm.get('newOrganization').value && !isNaN(+this.userCreateForm.get('organization').value)) {
      this.toastr.error(`L'organisation ${this.userCreateForm.get('organization').value} existe déjà.`);
      return false;
    }

    this.userCreateForm.patchValue({userRoles: this.userRolesSelected}, {emitEvent: false});

    const userValues = this.userCreateForm.value;

    this.store.dispatch(AddUserRequest({userInfos: userValues}));
    this.closeCreateUserDialog();

  }

  displayFirstNameErrorMsg(): string {
    return this.userCreateForm.get('firstName').hasError('required') ? this.errorMessages.firstName.required : this.errorMessages.firstName.length;
  }

  displayLastNameErrorMsg(): string {
    return this.userCreateForm.get('lastName').hasError('required') ? this.errorMessages.lastName.required : this.errorMessages.lastName.length;
  }

  displayEmailErrorMsg(): string {
    if (this.userCreateForm.get('email').hasError('email')) {
      return this.errorMessages.email.format;
    }
    if (this.userCreateForm.get('email').hasError('required')) {
      return this.errorMessages.email.required;
    }
    return this.userCreateForm.get('email').hasError('length') ? this.errorMessages.email.length : this.errorMessages.email.unique;
  }

  displayOrganizationErrorMsg(): string {
    return this.userCreateForm.get('organization').hasError('required') ? this.errorMessages.organization.required : this.errorMessages.organization.length;
  }

  displayPasswordErrorMsg(): string {
    return this.userCreateForm.get('password').hasError('required') ? this.errorMessages.password.required : this.errorMessages.password.length;
  }

  organizationDisplayFn(orgId): string {
    orgId = orgId ? this.organizations.find(org => org.id === orgId)?.name : null;
    return orgId;
  }

  displayAccountTypeErrorMsg(): string {
    return this.errorMessages.accountType.required;
  }

  private async uniqueEmailValidator(control: UntypedFormControl) {
    if (!control.value) {
      return null;
    }
    const response = await this.usersListService.checkIfUserExists(control.value).toPromise();
    return !response.data.datas.exist ? null : {validateEmail: {valid: false}};
  }

  userRolesChanged(e: string[]) {
    this.userRolesSelected = e;
  }

}
